'use client';

import * as React from 'react';
import Link from 'next/link';
import { SiteNav } from './SiteNav';
import { SiteFooter } from './SiteFooter';
import { FlowSim } from './FlowSim';
import { DashboardMock } from './DashboardMock';
import { PipelineMock } from './PipelineMock';
import { JourneyMock } from './JourneyMock';
import { ValuationSceneMock, VALUATION_LOOP_MS } from './ValuationSceneMock';
import { LogoWall } from './clientLogos';
import { Highlight } from './Highlight';
import { useScrollReveal } from './useScrollReveal';
import { SectionHeading, TestimonialGrid, PILLAR_BG_CLASSES } from './SectionPrimitives';
import { ArrowIcon, LayersIcon, BoltIcon, ChartIcon } from './icons';
import { Cap } from './Cap';
import type { CultivCopy } from '../types';
import { demoHref, cultivPath } from '../site';

/* one full loop per scene mock: 01 pipeline 31s, 02 journey 34s, 03 valuation 33s, 04 dashboard 14s */
const SHOWCASE_TAB_DURATIONS_MS = [31000, 34000, VALUATION_LOOP_MS, 14000];

/* hero media band: chapter tabs on the seam, product floats on pixel signal */
function HeroShowcase({ copy, locale }: { copy: CultivCopy; locale: string }) {
  const TABS = copy.home.tabs.map((t, i) => ({ n: ['01', '02', '03', '04'][i], label: t.label }));
  const [active, setActive] = React.useState(0);
  React.useEffect(() => {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = window.setTimeout(() => setActive((a) => (a + 1) % TABS.length), SHOWCASE_TAB_DURATIONS_MS[active]);
    return () => window.clearTimeout(id);
  }, [active, TABS.length]);
  return (
    <div className="b-v2-stage">
      <div className="b-v2-tabs" role="tablist" aria-label={copy.home.showcaseAriaLabel}>
        {TABS.map((tab, i) => (
          <button
            key={i}
            id={'b-v2-tab-' + i}
            role="tab"
            aria-selected={i === active}
            aria-controls="b-v2-panel"
            className={'b-v2-tab' + (i === active ? ' on' : '')}
            onClick={() => setActive(i)}
          >
            <span className="bar">
              <i key={active} style={{ ['--bar-dur' as string]: SHOWCASE_TAB_DURATIONS_MS[active] + 'ms' } as React.CSSProperties} />
            </span>
            <span className="n">{tab.n}</span>
            {tab.label}
          </button>
        ))}
      </div>
      <div className="b-v2-band b-v2-band--tight">
        {PILLAR_BG_CLASSES.map((bg, i) => (
          <div key={bg} className={'bg ' + bg + (i === active ? ' on' : '')} />
        ))}
        <div className="b-v2-band-in">
          {/* every tab is a self-framed scene canvas — no browser frame */}
          <div id="b-v2-panel" role="tabpanel" aria-labelledby={'b-v2-tab-' + active} key={active}>
            {active === 0 ? (
              <PipelineMock dash={copy.dash} pipe={copy.pipe} />
            ) : active === 1 ? (
              <JourneyMock dash={copy.dash} journey={copy.journey} />
            ) : active === 2 ? (
              <ValuationSceneMock avm={copy.avmScene} locale={locale} />
            ) : (
              <DashboardMock dash={copy.dash} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LandingHomePage({ copy, locale }: { copy: CultivCopy; locale: string }) {
  useScrollReveal();

  return (
    <div className="b-root" style={{ width: '100%' }}>
      <a className="b-skip" href="#content">
        {copy.common.skipToContent}
      </a>
      <SiteNav copy={copy} locale={locale} />
      {/* the agency chat widget is mounted for ALL landing routes in cultiv/layout.tsx */}

      <main id="content">
        {/* HERO — centered stack (same .cen variant as Customers):
            two-tone display, lede, CTA duo side by side */}
        <div className="b-v2-hero cen">
          <div className="b-wrap">
            <h1>
              <Highlight text={copy.home.heroTitle} />
            </h1>
            <p className="lede">{copy.home.lede}</p>
            <div className="b-v2-hero-cta b-cta-duo">
              <Link className="b-btn orange lg" href={demoHref(locale)}>
                <Cap>{copy.common.demoCta}</Cap> <ArrowIcon size={18} style={{ color: '#fff' }} />
              </Link>
              <Link className="b-btn ghost lg" href={cultivPath(locale, '/platform')}>
                {copy.common.secondaryCta}
              </Link>
            </div>
          </div>
        </div>

        {/* hero media band */}
        <HeroShowcase copy={copy} locale={locale} />

        {/* clients — static hairline logo wall, all marks at equal visual weight */}
        <div className="b-home-ticker">
          <p className="b-ticker-head">
            <Highlight text={copy.home.ticker} />
          </p>
          <div className="b-wrap">
            <LogoWall />
          </div>
        </div>

        {/* FLOW — before / after Cultiv (title lives inside the pinned panel so it stays visible) */}
        <div className="b-section">
          <div className="b-wrap">
            <FlowSim flow={copy.flow} scrollDrive height={560} title={copy.home.flowTitle} />
          </div>
        </div>

        {/* objection — "not one more tool" */}
        <div className="b-section b-band-soft">
          <div className="b-wrap">
            <SectionHeading title={copy.home.objTitle} center />
            <div className="b-cards3" style={{ marginTop: 44 }}>
              {[
                { icon: <LayersIcon size={24} />, ...copy.home.objCards[0] },
                { icon: <BoltIcon size={24} />, ...copy.home.objCards[1] },
                { icon: <ChartIcon size={24} />, ...copy.home.objCards[2] },
              ].map((card, i) => (
                <div className="b-c3" key={i}>
                  <div className="ic">{card.icon}</div>
                  <h3>{card.title}</h3>
                  <p>{card.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* testimonials */}
        <div className="b-section">
          <div className="b-wrap">
            <TestimonialGrid items={copy.testi.map((q, i) => (i === 0 ? { ...q, featured: true } : q))} />
          </div>
        </div>

        {/* close — off-white plate floating on a warm shopping street (matches products/customers) */}
        <div className="b-section flush b-v2-ctaband">
          <div className="b-v2-band">
            <div className="bg on street" />
            <div className="b-v2-band-in">
              <div className="b-v2-plate">
                <h2>
                  <Highlight text={copy.home.ctaTitle} />
                </h2>
                <p>{copy.home.ctaSub}</p>
                <div className="row">
                  <Link className="b-btn orange lg" href={demoHref(locale)}>
                    <Cap>{copy.common.demoCta}</Cap> <ArrowIcon size={18} style={{ color: '#fff' }} />
                  </Link>
                  <Link className="b-btn ghost lg" href={cultivPath(locale, '/platform')}>
                    {copy.common.secondaryCta}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <SiteFooter copy={copy} locale={locale} />
    </div>
  );
}
